import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { closeModal } from '../../actions/ModalActions';
import Modal from '../Modal';
import discountApi from './discountApi';
import { percentToRate } from '../../utils/discountFormat';

/**
 * Create a coupon — the reusable terms any number of promotion codes can
 * give away — or edit one that is still a draft. Laid out like Stripe's
 * "Create coupon": name, type, percentage, products, duration, redemption
 * limits. Terms lock on activation, so an active coupon is never
 * editable (the API refuses it either way). The code, countries, dates
 * and commission impact are campaign detail and live on the promotion.
 */
function productKey(product) {
    return `${product.type}|${product.name}`;
}

/** Seat types (price rows inside a plan) share the picker with products; scope=user_type. */
function seatKey(userType) {
    return `user_type|${userType.key}`;
}

/** "0.250000" -> "25", "1.0000" -> "100" */
function toPercentField(rate) {
    return rate === null || rate === undefined || rate === '' ? '' : String(Number((Number(rate) * 100).toFixed(2)));
}

/**
 * Stored targets are one row per product_id (plus the 'sms' pseudo-key);
 * the picker works in "type|name" keys, so fold them back up.
 */
function targetKeys(coupon, reference) {
    var seen = {};
    (coupon.targets || []).forEach(t => {
        if (t.target_type === 'user_type') {
            seen[`user_type|${t.target_key}`] = true;
            return;
        }
        if (t.target_key === reference.sms_product_key) {
            seen[reference.sms_product_key] = true;
            return;
        }
        var product = reference.products.find(p => (p.product_ids || []).includes(t.target_key));
        seen[product ? productKey(product) : t.target_key] = true;
    });

    return Object.keys(seen);
}

@connect(
    () => ({}),
    dispatch => bindActionCreators({ closeModal }, dispatch)
)
export default class CouponFormModal extends Component {
    constructor(props) {
        super(props);
        var coupon = props.coupon;
        this.state = {
            isSaving: false,
            error: null,
            form: coupon ? {
                invoice_name: coupon.invoice_name || '',
                percent: toPercentField(coupon.percent_off),
                specific_products: coupon.scope === 'product' || coupon.scope === 'user_type',
                targets: targetKeys(coupon, props.reference),
                duration_type: coupon.duration_type || 'fixed_period',
                duration_unit: coupon.duration_unit || 'month',
                duration_count: coupon.duration_count || 3,
                limit_date: !!coupon.redeem_by,
                redeem_by: coupon.redeem_by ? coupon.redeem_by.slice(0, 10) : '',
                limit_total: coupon.max_redemptions !== null && coupon.max_redemptions !== undefined,
                max_redemptions: coupon.max_redemptions === null || coupon.max_redemptions === undefined ? '' : String(coupon.max_redemptions),
                commission_impact: toPercentField(coupon.commission_impact_ratio)
            } : {
                invoice_name: '',
                percent: '',
                specific_products: false,
                targets: [],
                duration_type: 'fixed_period',
                duration_unit: 'month',
                duration_count: 3,
                limit_date: false,
                redeem_by: '',
                limit_total: false,
                max_redemptions: '',
                commission_impact: '100'
            }
        };
    }

    set(field, value) {
        this.setState({ form: { ...this.state.form, [field]: value } });
    }

    toggleTarget(key) {
        var targets = this.state.form.targets;
        this.set('targets', targets.includes(key) ? targets.filter(t => t != key) : targets.concat([key]));
    }

    handleSubmit() {
        var f = this.state.form;
        var seatTypes = f.targets.filter(k => k.indexOf('user_type|') === 0);
        var body = {
            invoice_name: f.invoice_name,
            percent_off: percentToRate(f.percent),
            scope: !f.specific_products ? 'account' : (seatTypes.length > 0 ? 'user_type' : 'product'),
            duration_type: f.duration_type,
            max_redemptions: f.limit_total && f.max_redemptions ? Number(f.max_redemptions) : null,
            redeem_by: f.limit_date && f.redeem_by ? f.redeem_by + ' 23:59:59' : null,
            commission_impact_ratio: (Number(f.commission_impact) / 100).toFixed(4)
        };
        // Always sent: on edit an empty list is how a coupon that used to be
        // product-scoped drops its now-invalid target rows.
        body.targets = [];
        if (f.specific_products) {
            // A picked product name expands to every product_id behind it
            f.targets.forEach(key => {
                if (key.indexOf('user_type|') === 0) {
                    body.targets.push({ target_type: 'user_type', target_key: key.slice('user_type|'.length) });
                    return;
                }
                if (key === this.props.reference.sms_product_key) {
                    body.targets.push({ target_type: 'product', target_key: key });
                    return;
                }
                var product = this.props.reference.products.find(p => productKey(p) === key);
                (product ? product.product_ids : [key]).forEach(id => body.targets.push({ target_type: 'product', target_key: id }));
            });
        }
        if (f.duration_type === 'fixed_period') {
            body.duration_unit = f.duration_unit;
            body.duration_count = Number(f.duration_count);
        }

        this.setState({ isSaving: true, error: null });
        var coupon = this.props.coupon;
        if (coupon) {
            // Optimistic locking: the API answers 409 if someone else
            // changed the coupon since this modal opened.
            body.version = coupon.version;
        }
        var request = coupon
            ? discountApi.updateCoupon(coupon.coupon_id, body)
            : discountApi.createCoupon(body);
        request
            .then(saved => {
                this.props.closeModal();
                if (this.props.onDone) {
                    this.props.onDone(saved);
                }
            })
            .catch(error => this.setState({ isSaving: false, error: error.message }));
    }

    render() {
        var { form, isSaving, error } = this.state;
        var ref = this.props.reference;
        var isEdit = !!this.props.coupon;
        var productOptions = [{ key: ref.sms_product_key, label: 'SMS blocks' }]
            .concat(ref.products.map(p => ({ key: productKey(p), label: `${p.name}${p.type === 'add-on' ? ' (add-on)' : ''}` })));
        var seatOptions = (ref.user_types || []).map(u => ({ key: seatKey(u), label: u.name }));
        var pickedSeats = form.targets.filter(k => k.indexOf('user_type|') === 0).length;
        var pickedProducts = form.targets.length - pickedSeats;
        var mixed = pickedSeats > 0 && pickedProducts > 0;
        var canSubmit = form.invoice_name && percentToRate(form.percent) !== null
            && (!form.specific_products || (form.targets.length > 0 && !mixed))
            && (form.duration_type !== 'fixed_period' || Number(form.duration_count) >= 1)
            && (!form.limit_date || form.redeem_by)
            && (!form.limit_total || Number(form.max_redemptions) >= 1)
            && !isSaving;
        var actionButton = (
            <button className={`btn btn-success ${canSubmit ? '' : 'disabled'}`} onClick={canSubmit ? this.handleSubmit.bind(this) : undefined}>
                {isSaving ? 'Saving...' : (isEdit ? 'Save coupon' : 'Create coupon')}
            </button>
        );
        var section = title => <h5 style={{ marginTop: 18, marginBottom: 8, textTransform: 'uppercase', fontSize: 11, letterSpacing: 1 }} className="text-muted">{title}</h5>;

        return (
            <Modal actionButton={actionButton} className="modal-primary" title={isEdit ? 'Edit coupon' : 'Create coupon'}>
                {error && <div className="alert alert-danger">{error}</div>}

                <div className="form-group">
                    <label>Name</label>
                    <input className="form-control" value={form.invoice_name} maxLength={200} placeholder="e.g. TIMESHEET20OFF" onChange={e => this.set('invoice_name', e.target.value)} />
                    <small className="text-muted">This will appear on customers' receipts and invoices.</small>
                </div>

                {section('Discount')}
                <div className="row">
                    <div className="col-xs-6">
                        <div className="form-group">
                            <label>Type</label>
                            <div className="form-control-static">Percentage off</div>
                        </div>
                    </div>
                    <div className="col-xs-6">
                        <div className={`form-group ${form.percent !== '' && percentToRate(form.percent) === null ? 'has-error' : ''}`}>
                            <label>Percentage discount</label>
                            <div className="input-group">
                                <input type="number" min={1} max={100} step={1} className="form-control" value={form.percent} onChange={e => this.set('percent', e.target.value)} />
                                <span className="input-group-addon">%</span>
                            </div>
                            <small className="help-block">Whole percent, 1 to 100 (V1 stores rates to 2 decimal places)</small>
                        </div>
                    </div>
                </div>
                <div className="checkbox">
                    <label>
                        <input type="checkbox" checked={form.specific_products} onChange={e => this.set('specific_products', e.target.checked)} /> Apply to specific products
                    </label>
                </div>
                {form.specific_products &&
                    <div className="form-group" style={{ marginLeft: 20 }}>
                        <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 3, padding: '4px 10px' }}>
                            <div className="text-muted" style={{ marginTop: 6 }}><small><strong>Products</strong></small></div>
                            {productOptions.map(o =>
                                <div key={o.key} className="checkbox" style={{ marginTop: 4, marginBottom: 4 }}>
                                    <label>
                                        <input type="checkbox" checked={form.targets.includes(o.key)} onChange={() => this.toggleTarget(o.key)} /> {o.label}
                                    </label>
                                </div>
                            )}
                            {seatOptions.length > 0 && <div className="text-muted" style={{ marginTop: 10 }}><small><strong>Seat types</strong> <span>— the user types inside a plan</span></small></div>}
                            {seatOptions.map(o =>
                                <div key={o.key} className="checkbox" style={{ marginTop: 4, marginBottom: 4 }}>
                                    <label>
                                        <input type="checkbox" checked={form.targets.includes(o.key)} onChange={() => this.toggleTarget(o.key)} /> {o.label}
                                    </label>
                                </div>
                            )}
                        </div>
                        {mixed
                            ? <small className="text-danger">Pick products or seat types, not both — "20% off Timesheet users" and "25% off Certificates" are two coupons.</small>
                            : <small className="text-muted">{form.targets.length} selected. Only these lines are discounted; the rest of the invoice is charged in full.</small>}
                    </div>
                }

                {section('Duration')}
                <div className="row">
                    <div className="col-xs-6">
                        <div className="form-group">
                            <select className="form-control" value={form.duration_type} onChange={e => this.set('duration_type', e.target.value)}>
                                <option value="once">Once</option>
                                <option value="fixed_period">Multiple months</option>
                                <option value="lifetime">Forever</option>
                            </select>
                            <small className="text-muted">This determines how long this coupon will apply once redeemed.</small>
                        </div>
                    </div>
                    {form.duration_type === 'fixed_period' &&
                        <div className="col-xs-3">
                            <div className="form-group">
                                <input type="number" min={1} className="form-control" value={form.duration_count} onChange={e => this.set('duration_count', e.target.value)} />
                            </div>
                        </div>
                    }
                    {form.duration_type === 'fixed_period' &&
                        <div className="col-xs-3">
                            <div className="form-group">
                                <select className="form-control" value={form.duration_unit} onChange={e => this.set('duration_unit', e.target.value)}>
                                    <option value="month">months</option>
                                    <option value="day">days</option>
                                </select>
                            </div>
                        </div>
                    }
                </div>
                {form.duration_type === 'once' && <p className="text-muted"><small>One invoice only: the rest of the calendar month the discount starts in.</small></p>}
                {form.duration_type === 'fixed_period' && <p className="text-muted"><small>Windows are inclusive calendar dates and end the day before the same date {form.duration_count || 'N'} {form.duration_unit}{form.duration_count == 1 ? '' : 's'} after the start.</small></p>}

                {section('Redemption limits')}
                <div className="checkbox">
                    <label>
                        <input type="checkbox" checked={form.limit_date} onChange={e => this.set('limit_date', e.target.checked)} /> Limit the date range when customers can redeem this coupon
                    </label>
                </div>
                {form.limit_date &&
                    <div className="form-group" style={{ marginLeft: 20, maxWidth: 220 }}>
                        <input type="date" className="form-control" value={form.redeem_by} onChange={e => this.set('redeem_by', e.target.value)} />
                        <small className="text-muted">last day it can be applied or redeemed</small>
                    </div>
                }
                <div className="checkbox">
                    <label>
                        <input type="checkbox" checked={form.limit_total} onChange={e => this.set('limit_total', e.target.checked)} /> Limit the total number of times this coupon can be redeemed
                    </label>
                </div>
                {form.limit_total &&
                    <div className="form-group" style={{ marginLeft: 20, maxWidth: 220 }}>
                        <input type="number" min={1} className="form-control" placeholder="e.g. 50" value={form.max_redemptions} onChange={e => this.set('max_redemptions', e.target.value)} />
                        <small className="text-muted">across every promotion code on this coupon</small>
                    </div>
                }

                {section('Internal')}
                <div className="form-group" style={{ maxWidth: 220 }}>
                    <label>Commission impact</label>
                    <div className="input-group">
                        <input type="number" min={0} max={100} className="form-control" value={form.commission_impact} onChange={e => this.set('commission_impact', e.target.value)} />
                        <span className="input-group-addon">%</span>
                    </div>
                    <small className="text-muted">Default for this coupon; a promotion code can override it. Sales promos 100, marketing / CX 0.</small>
                </div>
            </Modal>
        );
    }
}



// WEBPACK FOOTER //
// ./app/components/discounts/CouponFormModal.js